<script setup>
import { ref, computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import ColorPicker from 'primevue/colorpicker';

const { ajout, couleurInitiale } = defineProps({
    ajout: Boolean,
    couleurInitiale: Object
});

const visible = defineModel('visible', {
    type: Boolean
});

const emit = defineEmits(['validation']);

const couleur = ref(couleurInitiale);
const rouge = ref(couleurInitiale?.r);
const vert = ref(couleurInitiale?.g);
const bleu = ref(couleurInitiale?.b);
const codeHexadecimal = ref(convertirRgbEnHexadecimal(couleurInitiale));

const titreModale = computed(() => {
    return (ajout ? 'Ajout d\'une couleur' : 'Édition d\'une couleur');
});

watch(() => couleur.value, (couleur) => {
    rouge.value = couleur?.r;
    vert.value = couleur?.g;
    bleu.value = couleur?.b;
    codeHexadecimal.value = convertirRgbEnHexadecimal(couleur);
});

watch(() => {return {r: rouge.value, g: vert.value, b: bleu.value};}, (couleurModifiee) => {
    couleur.value = couleurModifiee;
});

watch(() => codeHexadecimal.value, (code) => {
    const conversion = convertirHexadecimalEnRgb(code);
    if (conversion) {
        couleur.value = conversion;
    }
});

function convertirRgbEnHexadecimal(couleurRgb) {
    if (couleurRgb) {
        return "#" + convertirNbEnHexadecimal(couleurRgb.r) + convertirNbEnHexadecimal(couleurRgb.g) + convertirNbEnHexadecimal(couleurRgb.b);
    } else {
        return null;
    }
}

function convertirNbEnHexadecimal(nombre) {
    if (nombre) {
        const hexadecimal = nombre.toString(16);
        return hexadecimal.length == 1 ? '0' + hexadecimal : hexadecimal;
    } else {
        return '00';
    }
}

function convertirHexadecimalEnRgb(code) {
    const regex = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
    const matching = regex.exec(code);
    if (matching) {
        return {r: parseInt(matching[1], 16), g: parseInt(matching[2], 16), b: parseInt(matching[3], 16)};
    } else {
        return null;
    }
}

function valider() {
    emit('validation', couleur.value);
    visible.value = false;
}

</script>

<template>
    <Dialog 
        v-model:visible="visible" 
        modal 
        :header="titreModale">
            <div class="contenu-modale" @keydown.enter.prevent="valider()">
                <ColorPicker 
                  class="color-picker"
                  inline
                  format="rgb" 
                  v-model="couleur" />
                <div class="couleur-selectionnee" :style="{backgroundColor: `rgb(${couleur.r}, ${couleur.g}, ${couleur.b})`}"/>
                <div class="zone-valeurs-rgb">
                    <label class="intitule">Rouge</label>
                    <input type="number" min="0" max="255" class="selecteur-valeur-rgb" v-model="rouge"/>
                    <label class="intitule">Vert</label>
                    <input type="number" min="0" max="255" class="selecteur-valeur-rgb" v-model="vert" />
                    <label class="intitule">Bleu</label>
                    <input type="number" min="0" max="255" class="selecteur-valeur-rgb" v-model="bleu" />
                </div>
                <div class="zone-valeurs-hex">
                    <label class="intitule">Code hexadécimal</label>
                    <input type="text" class="selecteur-valeur-hex" v-model="codeHexadecimal"/>
                </div>
            </div>
            <template #footer>
                <button 
                    class="bouton fas fa-check" 
                    :disabled="false"
                    @click="valider()"/>
            </template>
    </Dialog>
</template>

<style scoped>

.contenu-modale {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.zone-valeurs-rgb {
    display: flex;
    flex-direction: row;
    margin-top: 20px;
}

.zone-valeurs-hex {
    display: flex;
    flex-direction: row;
    margin-top: 10px;
    align-self: flex-start;
}

.selecteur-valeur-rgb {
    margin-left: 5px;
    margin-right: 20px;
    width: 45px;
}

.selecteur-valeur-hex {
    margin-left: 5px;
    width: 60px;
}

.couleur-selectionnee {
    width: 50px;
    height: 15px;
    margin-top: 10px;
    border: 1px solid black;
}

</style>